import { Box, Alert } from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { EncoreFormRenderer } from '@locus-taxy/encore-form';
import { useClientId } from '../../hooks/useClientId';
import { GOOGLE_MAPS_API_KEY } from '../../config';
import type { EntityDetailsFormProps } from './types';
import { useMemo, useEffect, useState } from 'react';
import LoadingSpinner from '../LoadingSpinner';

export default function EntityDetailsForm({
  entityName,
  entityData,
  detailsForm,
  isLoading,
  error,
}: Readonly<EntityDetailsFormProps>) {
  const { t } = useTranslation();

  const additionalContext = useMemo(
    () => ({
      entityData,
      ...entityData, // TODO: Remove this once we have this fixed in BE
    }),
    [entityData]
  );

  // Track entityData changes to force form remount when data updates
  const [formKey, setFormKey] = useState(0);

  // Increment key whenever entityData reference changes (which happens after refetch)
  useEffect(() => {
    setFormKey(prev => prev + 1);
  }, [entityData]);

  const clientId = useClientId();
  if (isLoading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('entityDetails.errorLoadingDetails')}
      </Alert>
    );
  }

  // Check if we have a valid schema with components
  if (!detailsForm?.components || !Array.isArray(detailsForm?.components)) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {t('entityDetails.noFormSchema')}
      </Alert>
    );
  }

  if (!entityData) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {t('entityDetails.noEntityData')}
      </Alert>
    );
  }
  return (
    <Box sx={{ p: 2, height: '100%' }}>
      <EncoreFormRenderer
        key={formKey}
        schema={detailsForm as any}
        clientId={clientId}
        initialData={entityData}
        additionalContext={additionalContext}
        googleApiKey={GOOGLE_MAPS_API_KEY}
        autoFit
        routeContext={{
          entity: entityName,
          page: 'entity-details',
        }}
      />
    </Box>
  );
}
